import React, { useState, useEffect } from "react";
import { Campaign } from "../../../data/campaignData";
import {
  UsersIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ArrowTrendingUpIcon2,
} from "./ChartIcons";
import { apiService, CampaignsTopStatResponse } from "../../../services";

interface StatisticsCardsProps {
  campaigns: Campaign[];
}

const StatisticsCards: React.FC<StatisticsCardsProps> = ({ campaigns }) => {
  const [topStats, setTopStats] = useState<
    CampaignsTopStatResponse["data"] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load statistics from getTopCampaignsStats API
  useEffect(() => {
    const loadTopStats = async () => {
      try {
        setIsLoading(true);
        console.log("StatisticsCards: Loading top campaign stats from API");
        const response = await apiService.getTopCampaignsStats();
        if (response.success) {
          setTopStats(response.data);
          console.log("StatisticsCards: Loaded top stats:", response.data);
        }
      } catch (error) {
        console.error("Failed to load top campaign stats:", error);
        setTopStats(null);
      } finally {
        setIsLoading(false);
      }
    };

    // Only load fresh stats when component mounts
    loadTopStats();
  }, []); // Empty dependency array - only run on mount

  // Use API stats if available, otherwise fallback to calculated stats from campaigns
  const totalCampaigns = topStats?.total_campaign || campaigns.length;
  const activeCampaigns =
    topStats?.ongoing_campaign ||
    campaigns.filter((c) => c.status === "active").length;
  const endedCampaigns =
    topStats?.ended_campaign ||
    campaigns.filter((c) => c.status === "ended").length;

  // Chưa bắt đầu = Tổng - Đang hoạt động - Đã kết thúc
  const notStartedCampaigns = totalCampaigns - activeCampaigns - endedCampaigns;

  // Debug logging for statistics
  console.log("StatisticsCards: Using statistics:", {
    source: topStats ? "API getTopCampaignsStats" : "Calculated from campaigns",
    totalCampaigns,
    activeCampaigns,
    endedCampaigns,
    notStartedCampaigns,
    topStats: topStats
      ? {
          total_campaign: topStats.total_campaign,
          ongoing_campaign: topStats.ongoing_campaign,
          ended_campaign: topStats.ended_campaign,
        }
      : null,
  });

  const totalCodes = campaigns.reduce(
    (sum, campaign) => sum + campaign.totalCodes,
    0
  );
  const usedCodes = campaigns.reduce(
    (sum, campaign) => sum + campaign.usedCodes,
    0
  );
  const efficiencyRate =
    totalCodes > 0 ? ((usedCodes / totalCodes) * 100).toFixed(1) : "0";

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {isLoading && (
        <div className="col-span-full text-center py-2">
          <div className="inline-flex items-center text-sm text-blue-600 dark:text-blue-400">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            Đang cập nhật dữ liệu mới nhất...
          </div>
        </div>
      )}
      <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 px-4 py-4 rounded-xl border border-blue-200 dark:border-blue-800 transform hover:scale-105 transition-transform duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-blue-600 dark:text-blue-400 text-sm font-medium">Tổng chiến dịch</p>
            <p className="text-3xl font-bold">
              {totalCampaigns.toLocaleString()}
            </p>
          </div>
          <div className="bg-blue-200 dark:bg-blue-800 p-3 rounded-full">
            <UsersIcon />
          </div>
        </div>
      </div>
      <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 px-4 py-4 rounded-xl border border-purple-200 dark:border-purple-800 transform hover:scale-105 transition-transform duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-purple-600 dark:text-purple-400 text-sm font-medium">Chưa bắt đầu</p>
            <p className="text-3xl font-bold">
              {notStartedCampaigns.toLocaleString()}
            </p>
          </div>
          <div className="bg-purple-200 dark:bg-purple-800 p-3 rounded-full">
            <ArrowTrendingUpIcon />
          </div>
        </div>
      </div>
      <div className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-4 py-4 rounded-xl border border-green-200 dark:border-green-800 transform hover:scale-105 transition-transform duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-green-600 dark:text-green-400 text-sm font-medium">Đang hoạt động</p>
            <p className="text-3xl font-bold">
              {activeCampaigns.toLocaleString()}
            </p>
          </div>
          <div className="bg-green-200 dark:bg-green-800 p-3 rounded-full">
            <ArrowTrendingUpIcon2 />
          </div>
        </div>
      </div>

      <div className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200 px-4 py-4 rounded-xl border border-orange-200 dark:border-orange-800 transform hover:scale-105 transition-transform duration-300">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-orange-600 dark:text-orange-400 text-sm font-medium">Đã kết thúc</p>
            <p className="text-3xl font-bold">
              {endedCampaigns.toLocaleString()}
            </p>
          </div>
          <div className="bg-orange-200 dark:bg-orange-800 p-3 rounded-full">
            <CalendarIcon />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsCards;
